import { type ReactNode } from 'react';
import { Button, DialogShell } from '@sentinel/ui';

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  subtitle?: string;
  /** Form/body content; the dialog handles surrounding chrome. */
  children?: ReactNode;
  submitLabel: string;
  cancelLabel: string;
  /** Disabled state for the submit button (e.g. while a mutation is in flight). */
  submitting?: boolean;
  /** Variant `danger` styles the submit button as destructive. */
  variant?: 'default' | 'danger';
  /** Form id; the children should render an <input> bound to this form. */
  formId?: string;
  onSubmit?: () => void;
  onCancel: () => void;
}

/**
 * Lightweight confirmation modal. Composes <DialogShell> from @sentinel/ui
 * for the dark surface, focus trap, escape-to-close, and shadow ramp.
 */
export function ConfirmDialog({
  open,
  title,
  subtitle,
  children,
  submitLabel,
  cancelLabel,
  submitting,
  variant = 'default',
  formId,
  onSubmit,
  onCancel,
}: ConfirmDialogProps) {
  return (
    <DialogShell
      open={open}
      onCancel={onCancel}
      title={title}
      description={subtitle}
      width="narrow"
    >
      {children}
      <DialogShell.Footer>
        <Button type="button" variant="secondary" onClick={onCancel} disabled={submitting}>
          {cancelLabel}
        </Button>
        {formId ? (
          <Button
            type="submit"
            form={formId}
            variant={variant === 'danger' ? 'danger' : 'primary'}
            disabled={submitting}
          >
            {submitLabel}
          </Button>
        ) : (
          <Button
            type="button"
            variant={variant === 'danger' ? 'danger' : 'primary'}
            onClick={onSubmit}
            disabled={submitting}
          >
            {submitLabel}
          </Button>
        )}
      </DialogShell.Footer>
    </DialogShell>
  );
}
